/**
 * BaselineRefreshDialog — confirms a baseline refresh for one tenant (the
 * trigger lives on the Tenants page). A Carbon danger modal: refreshing is
 * irreversible, so the primary action takes Coral and the dialog is an
 * alertdialog (components/carbon/Modal). The primary stays disabled until the
 * acknowledgement box is ticked, and the dialog can't be dismissed while the
 * request is in flight.
 */

import { useState } from 'react'
import { useMsal } from '@azure/msal-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { Checkbox } from '@/components/carbon/Checkbox'
import { Modal } from '@/components/carbon/Modal'

interface Props {
  isOpen: boolean
  onClose: () => void
  tenantId: string
  onSuccess: () => void
}

export default function BaselineRefreshDialog({ isOpen, onClose, tenantId, onSuccess }: Props) {
  const [isLoading, setIsLoading] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const { accounts } = useMsal()

  const handleRefresh = async () => {
    if (!acknowledged) {
      toast.error('Select the confirmation box first.')
      return
    }

    // Identify the requester for the audit log. MSAL's `account.username`
    // is the UPN/email for AAD-authenticated users — matches the convention
    // used by useSecurityBrief / useComplianceBrief / useIdentityBrief and
    // by what the Container's RequestedBy column expects (NVARCHAR up to
    // 200 chars). Fall back to display name, then a generic placeholder
    // only as a last resort, so we never block the action on a missing
    // identity but also never silently log 'Current User'.
    const account = accounts[0]
    const requestedBy =
      account?.username ?? account?.name ?? 'unknown-user'

    setIsLoading(true)
    try {
      await api.refreshBaseline(tenantId, requestedBy)

      toast.success('Baseline refresh started.')
      onSuccess()
      onClose()
    } catch (error: any) {
      // Handle 409 conflict specifically
      if (error.response?.status === 409) {
        const message = error.response?.data?.message || 'A scan or baseline refresh is already running for this tenant. Wait for it to finish.'
        toast.error(message, { duration: 6000 })
      } else {
        toast.error(error.response?.data?.error || error.response?.data?.message || 'Baseline refresh could not start. Try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setAcknowledged(false)
      onClose()
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="sm"
      danger
      title="Refresh the baseline"
      subtitle="This captures your current Microsoft 365 configuration as the new baseline."
      primaryAction={{
        label: isLoading ? 'Refreshing the baseline' : 'Refresh baseline',
        onClick: () => void handleRefresh(),
        disabled: !acknowledged,
        loading: isLoading,
      }}
      secondaryAction={{ label: 'Cancel', onClick: handleClose, disabled: isLoading }}
    >
      <div className="flex flex-col gap-4 text-body text-text-secondary">
        <p className="m-0">
          Every open drift that matches the new baseline is marked{' '}
          <b className="font-semibold text-text-primary">Remediated</b> and leaves your drift list.
        </p>
        <p className="m-0">
          <b className="font-semibold text-text-primary">This action cannot be undone.</b>
        </p>
        <Checkbox
          id="acknowledge"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          disabled={isLoading}
          label="I understand the consequences and want to refresh the baseline"
          wrapperClassName="mt-2"
        />
      </div>
    </Modal>
  )
}
